import { useEffect, useRef } from 'react';
import {
  ATTENDANCE_STATUS,
  classifyDawdle,
  type AttendanceStatus,
  type DawdleSeverity,
} from '@projektarbeit/shared';
import { StatusBadge } from '@/components/StatusBadge';
import { DawdleBadge } from '@/components/DawdleBadge';
import { playSound, type SoundKey } from '@/lib/kiosk-audio';
import type { ScanResponse } from '@/hooks/useKiosk';
import { cn } from '@/lib/utils';

const OVERLAY_DURATION_MS = 3000;

export interface ScanResultOverlayProps {
  /** Successful scan result. Pass `null` to hide. */
  result: ScanResponse | null;
  onDismiss: () => void;
  dawdleWarnMinutes: number;
  dawdleAlertMinutes: number;
  /** Optional check-in time, shown as `HH:MM` in the result info box. */
  checkinTime?: string;
}

interface OverlayContent {
  badgeTone: 'success' | 'late' | 'logout' | 'switch';
  badgeLabel: string;
  greeting: string;
  // Either "Anmeldung erfolgreich" / "Verspätet angemeldet" → uses a room/time info box,
  // or "Ausgeloggt" / "Raumwechsel" → uses two-row info box.
  variant: 'check-in' | 'check-out' | 'room-switch';
}

function buildContent(result: ScanResponse, isLate: boolean): OverlayContent {
  const firstName = result.student.firstName;
  if (
    result.action === 'check-in' ||
    result.action === 'already-checked-in' ||
    result.action === 'room-switch'
  ) {
    if (result.action === 'room-switch') {
      return {
        badgeTone: 'switch',
        badgeLabel: 'Raumwechsel',
        greeting: `Hallo, ${firstName}!`,
        variant: 'room-switch',
      };
    }
    return {
      badgeTone: isLate ? 'late' : 'success',
      badgeLabel: isLate ? 'Verspätet angemeldet' : 'Anmeldung erfolgreich',
      greeting: `Hallo, ${firstName}!`,
      variant: 'check-in',
    };
  }
  return {
    badgeTone: 'logout',
    badgeLabel: 'Ausgeloggt',
    greeting: `Tschüss, ${firstName}!`,
    variant: 'check-out',
  };
}

const BADGE_TONE_CLASSES: Record<OverlayContent['badgeTone'], string> = {
  success: 'bg-status-present/15 text-status-present',
  late: 'bg-status-late/15 text-status-late',
  // Self-Logout in der Design-System-Primärfarbe (Petrol/Blau aus
  // `/docs/design/styles.css` — `--pa-primary`). Hebt den „Tschüss"-State
  // klar vom grünen Anwesend-State ab.
  logout: 'bg-primary/15 text-primary',
  switch: 'bg-primary/15 text-primary',
};

function pickSound(
  result: ScanResponse,
  severity: DawdleSeverity,
  attendanceStatus: AttendanceStatus | string,
): SoundKey {
  if (result.action === 'check-out' || result.action === 'still-checked-out') {
    return 'check-out';
  }
  // check-in / room-switch / already-checked-in
  if (severity === 'orange') return 'check-in-troedel-orange';
  if (severity === 'red') return 'check-in-troedel-rot';
  if (attendanceStatus === ATTENDANCE_STATUS.VERSPAETET) {
    return 'check-in-verspaetet';
  }
  return 'check-in-anwesend';
}

/**
 * In-column scan result panel per design mockup. Rendered inside the kiosk
 * middle column — NOT a fullscreen overlay anymore. Camera feedback (frame
 * colour + icon) is driven separately by the `feedback` prop on the compact
 * BarcodeScanner variant.
 *
 * Kept the `role="alertdialog"` + `aria-live` semantics: the consumer
 * (KioskPage) decides whether to render it or not, so dismissing is just
 * unmounting.
 */
export function ScanResultOverlay({
  result,
  onDismiss,
  dawdleWarnMinutes,
  dawdleAlertMinutes,
  checkinTime,
}: ScanResultOverlayProps): JSX.Element | null {
  // onDismiss in einem Ref kapseln, damit `useEffect` NICHT auf jede neue
  // Callback-Identität neu feuert. Sonst reicht ein Re-Render im
  // umgebenden KioskPage (z. B. Live-Clock alle 1 s) um den Timer
  // wegzucancellen und neu zu starten — das Overlay würde nie ablaufen.
  const onDismissRef = useRef(onDismiss);
  useEffect(() => {
    onDismissRef.current = onDismiss;
  }, [onDismiss]);

  useEffect(() => {
    if (!result) return;
    const timer = setTimeout(() => onDismissRef.current(), OVERLAY_DURATION_MS);
    return () => clearTimeout(timer);
  }, [result]);

  // Sound side-effect — runs once per result.
  useEffect(() => {
    if (!result) return;
    const dawdleMin = result.dawdleAtCheckin ?? 0;
    const severity =
      dawdleMin > 0 ? classifyDawdle(dawdleMin, dawdleWarnMinutes, dawdleAlertMinutes) : 'none';
    playSound(pickSound(result, severity, result.status));
  }, [result, dawdleWarnMinutes, dawdleAlertMinutes]);

  if (!result) return null;

  const isLate = result.status === ATTENDANCE_STATUS.VERSPAETET;
  const content = buildContent(result, isLate);
  const dawdleMin = result.dawdleAtCheckin ?? 0;
  const showDawdle =
    dawdleMin > 0 && (result.action === 'check-in' || result.action === 'room-switch');
  const severity = showDawdle
    ? classifyDawdle(dawdleMin, dawdleWarnMinutes, dawdleAlertMinutes)
    : 'none';
  const isAttendanceStatus = (
    [ATTENDANCE_STATUS.ANWESEND, ATTENDANCE_STATUS.VERSPAETET, ATTENDANCE_STATUS.FEHLT] as string[]
  ).includes(result.status);

  return (
    <div
      role="alertdialog"
      aria-live="assertive"
      aria-label="Scan-Ergebnis"
      data-action={result.action}
      className="anim-slide-up flex w-full max-w-md flex-col items-center gap-4 text-center"
    >
      <span
        className={cn(
          'inline-flex items-center gap-2 rounded-full px-4 py-1 text-sm font-semibold',
          BADGE_TONE_CLASSES[content.badgeTone],
        )}
      >
        <span
          aria-hidden
          className={cn(
            'h-2 w-2 rounded-full',
            content.badgeTone === 'late'
              ? 'bg-status-late'
              : content.badgeTone === 'switch' || content.badgeTone === 'logout'
                ? 'bg-primary'
                : 'bg-status-present',
          )}
        />
        {content.badgeLabel}
      </span>
      <h1 className="text-4xl font-bold leading-[1.05] tracking-tight md:text-5xl">
        {content.variant === 'room-switch'
          ? `🔄 ${result.previousRoom?.name ?? '?'} → ${result.room.name}`
          : content.greeting}
      </h1>
      <p className="text-base text-muted-foreground md:text-lg">
        {result.student.firstName} {result.student.lastName} · Klasse {result.student.className}
      </p>

      {content.variant === 'check-in' ? (
        <ResultInfoBox
          rows={[
            { label: 'Raum', value: result.room.name },
            ...(checkinTime ? [{ label: 'Zeit', value: checkinTime, mono: true }] : []),
          ]}
        />
      ) : content.variant === 'check-out' ? (
        <ResultInfoBox
          accent="primary"
          rows={[
            { label: 'Raum', value: result.room.name },
            ...(checkinTime ? [{ label: 'Abmeldung', value: checkinTime, mono: true }] : []),
          ]}
        />
      ) : null}

      <div className="flex flex-wrap items-center justify-center gap-2">
        {isAttendanceStatus ? <StatusBadge status={result.status as AttendanceStatus} /> : null}
        {showDawdle ? <DawdleBadge minutes={dawdleMin} severity={severity} /> : null}
      </div>
    </div>
  );
}

interface ResultInfoBoxRow {
  label: string;
  value: string;
  mono?: boolean;
}

function ResultInfoBox({
  rows,
  accent,
}: {
  rows: ResultInfoBoxRow[];
  /** `'primary'` zieht einen Petrol-Akzent links rein (für Self-Logout). */
  accent?: 'primary';
}): JSX.Element {
  return (
    <div
      className={cn(
        'inline-flex w-full flex-col gap-2 rounded-xl border bg-background/60 px-6 py-4 text-left',
        accent === 'primary' ? 'border-l-4 border-l-primary' : undefined,
      )}
    >
      {rows.map((row) => (
        <div key={row.label} className="flex items-center justify-between gap-6 text-base">
          <span className="text-muted-foreground">{row.label}</span>
          <span className={cn('font-semibold', row.mono ? 'font-mono' : undefined)}>
            {row.value}
          </span>
        </div>
      ))}
    </div>
  );
}
